/**
 * Turn dive conditions into a GoPro Labs command.
 *
 * The physics this encodes, briefly: water absorbs light from the long end of
 * the spectrum inward. Red is effectively gone by ~5 m, orange by ~10 m, yellow
 * by ~20 m. Salt water in the open ocean scatters blue; lake and temperate
 * coastal water carries phytoplankton and tannins that scatter green. So:
 *
 *   - salt water  -> the deficit is red. Gain red (WARM / WBDV).
 *   - green water -> the deficit is red *and* blue. Gain both, which is the
 *     digital equivalent of the magenta filter divers screw onto a housing.
 *   - below ~25 m -> there is no red left to recover from ambient light. Video
 *     lights become the only real fix; otherwise lean into the blue.
 *
 * Every value below is a defensible starting point, not a measured calibration.
 *
 * This file is a deliberate mirror of `backend/app/domain/dive_presets.py`.
 * They will drift; `src/lib/__tests__/engine.test.ts` holds the fixtures that
 * catch it. The long-term fix is the versioned JSON ruleset in DESIGN.md §2 —
 * one ruleset, two thin interpreters.
 */

import { supports } from "./cameras";
import type { Feature } from "./cameras";
import {
  DEFAULT_PRESET_ICON,
  EV_COMP_LABELS,
  type AspectRatio,
  type DiveConditions,
  type EvComp,
  type FrameRate,
  type Resolution,
} from "./conditions";
import { extension, setting, type Token } from "./tokens";

export interface ResolutionToken {
  text: string;
  label: string;
  rationale: string;
  feature?: Feature;
}

/** Which Labs `r` token each resolution/aspect combination emits, and which
 *  camera feature it needs (if any). Verified against GoPro's own reference
 *  (gopro.github.io/labs/control/tech/) — r5T is HERO10-only because HERO11+
 *  moved that sensor readout to 8:7 (r5X/r4X) instead, not a gap in this
 *  table. Exported so the UI can offer only what the selected camera
 *  actually supports, rather than offering everything and dropping the ones
 *  that don't fit at compile time. */
export const RESOLUTION_TOKENS: Record<Resolution, Partial<Record<AspectRatio, ResolutionToken>>> = {
  "5.3k": {
    "16:9": {
      text: "r5",
      label: "5.3K 16:9",
      rationale: "5.3K's standard widescreen frame — the most resolution without a sensor-specific crop.",
    },
    "4:3": {
      text: "r5T",
      label: "5.3K 4:3",
      rationale:
        "4:3 at full 5.3K resolution, using HERO10's own tall sensor readout. " +
        "HERO11 and up use the 8:7 mode instead.",
      feature: "sensorTallH10",
    },
    "8:7": {
      text: "r5X",
      label: "5.3K 8:7",
      rationale:
        "The tall 8:7 sensor area gives the most room to reframe a moving " +
        "subject or crop for vertical after the dive.",
      feature: "sensor87",
    },
  },
  "4k": {
    "16:9": {
      text: "r4",
      label: "4K 16:9",
      rationale: "16:9 at 4K is the smallest file here, and plenty of resolution for a straight edit.",
    },
    "4:3": {
      text: "r4T",
      label: "4K 4:3",
      rationale:
        "4:3 covers more of the water column than 16:9 without 5.3K's file size, " +
        "which suits wrecks and split shots.",
    },
    "8:7": {
      text: "r4X",
      label: "4K 8:7",
      rationale: "The same 8:7 reframing room as 5.3K 8:7, at 4K's smaller file size.",
      feature: "sensor87",
    },
  },
};

export interface Recommended {
  frameRate: FrameRate;
  resolution: Resolution;
  aspect: AspectRatio;
}

/**
 * The frame rate, resolution and aspect ratio this depth, subject and camera
 * call for. Applied whenever depth or subject changes (see App.tsx) as a
 * starting point — every field it sets is a normal chip the diver can
 * override afterward, and overriding it does not lock it: the next depth or
 * subject change recomputes and re-applies these again.
 */
export function recommend(c: DiveConditions): Recommended {
  // A slower frame rate is free light. Bright, shallow water can afford 60p;
  // anywhere else, 30p is the sweet spot (see the p{fps} rationale above).
  const frameRate: FrameRate = c.depthM <= 8 && c.visibility === "high" ? 60 : 30;

  if (c.subject === "wide") {
    // 8:7 needs the tall sensor readout; fall back to the widest thing every
    // camera has rather than recommend a combination this body will drop.
    return supports(c.camera, "sensor87")
      ? { frameRate, resolution: "5.3k", aspect: "8:7" }
      : { frameRate, resolution: "4k", aspect: "16:9" };
  }
  if (c.subject === "macro") {
    return { frameRate, resolution: "4k", aspect: "16:9" };
  }
  // wreck, surface: more of the water column than 16:9, without 5.3K's file size.
  return { frameRate, resolution: "4k", aspect: "4:3" };
}

/** Depth at which each part of the spectrum is effectively gone, in metres.
 *  R/O/Y/G are pure-water absorption and hold across every WaterType; blue's
 *  own depth is set per water below, since it is the one band the water
 *  itself changes. */
export const SPECTRUM: readonly { key: string; hex: string; goneAtM: number }[] =
  [
    { key: "R", hex: "#E03A2F", goneAtM: 5 },
    { key: "O", hex: "#E8752A", goneAtM: 12 },
    { key: "Y", hex: "#E5C233", goneAtM: 22 },
    { key: "G", hex: "#3FA65B", goneAtM: 34 },
    { key: "B", hex: "#2D6FD1", goneAtM: 70 },
  ];

/** Blue's own falloff depth, by water. Mirrors blueGainPercent: salt-clear
 *  water (tropical, and lake_clear alongside it) keeps blue past what matters
 *  here, but the tannin/algae water that already earns a blue gain in the
 *  Labs command loses blue much sooner — green_lake worse than
 *  temperate_salt, same ordering as blueGainPercent's scale factor. */
const BLUE_GONE_AT_M: Record<DiveConditions["water"], number> = {
  tropical_salt: 70,
  lake_clear: 70,
  temperate_salt: 45,
  lake_green: 25,
};

/** How much of each band survives to this depth, 0..1. */
export function spectrumAt(
  depthM: number,
  water: DiveConditions["water"],
): { key: string; hex: string; left: number }[] {
  return SPECTRUM.map((band) => ({
    key: band.key,
    hex: band.hex,
    left: Math.max(0, 1 - depthM / (band.key === "B" ? BLUE_GONE_AT_M[water] : band.goneAtM)),
  }));
}

/** How hard to push red, given depth, water, and whether lights are on. */
export function redGainPercent(c: DiveConditions): number {
  // Lights restore red optically. Gaining it again just goes orange.
  if (c.light === "video_lights") return 0;

  const base = {
    tropical_salt: 3.5,
    temperate_salt: 3.0,
    lake_clear: 2.5,
    lake_green: 2.0,
  }[c.water];

  let gain = base * Math.max(c.depthM - 3, 0);
  if (c.light === "mixed") gain *= 0.5;
  // Past here you are amplifying noise, not recovering colour.
  if (c.depthM > 30) gain *= 0.7;

  return Math.min(Math.round(gain / 5) * 5, 120);
}

/** Green water needs blue lifted alongside red. Blue water does not. */
export function blueGainPercent(c: DiveConditions): number {
  if (c.water !== "lake_green" && c.water !== "temperate_salt") return 0;
  if (c.light === "video_lights") return 0;
  const scale = c.water === "lake_green" ? 1.0 : 0.5;
  return Math.min(Math.round((2.0 * scale * Math.max(c.depthM - 2, 0)) / 5) * 5, 60);
}

/** GoPro's own reference writes `x-.5`, not `x-0.5` — no leading zero. A
 *  `Record<EvComp, string>` rather than a ternary chain: extending
 *  `EV_COMP_VALUES` in conditions.ts without adding an entry here is then a
 *  compile error instead of a silently wrong Labs command. */
const EV_TEXT: Record<EvComp, string> = {
  [-1]: "x-1",
  [-0.5]: "x-.5",
  [0]: "x0",
};

/** GoPro's own metering tends to overexpose underwater — bright sand, the
 *  surface, or a diver's fins fool it into pulling shadows up and blowing
 *  the rest out. `x<value>` is a plain camera setting (not persistence-gated
 *  like the extension tokens below), always emitted so a previous preset's
 *  pull can't leak into this one. */
function evToken(c: DiveConditions): Token {
  const v = c.evComp;
  return setting(
    EV_TEXT[v],
    v === 0 ? "No exposure pull" : EV_COMP_LABELS[v],
    v === 0
      ? "No exposure pull — the metering call stands as the camera makes it."
      : "Pulls exposure down to protect highlights the camera's own metering " +
          "would otherwise blow out underwater.",
    "ev",
  );
}

/** Labs format is i<max>M<min>, in hundreds of ISO. */
function isoToken(c: DiveConditions): Token {
  let max: number;
  let why: string;

  if (c.depthM <= 8 && c.visibility === "high") {
    max = 4;
    why = "Bright shallow water, so cap ISO at 400 and keep it clean.";
  } else if (c.depthM <= 20) {
    max = 8;
    why = "Cap at ISO 800: enough latitude for mid-water without visible grain.";
  } else if (c.depthM <= 32) {
    max = 16;
    why = "Deep and dim, so allow ISO 1600. Expect noise in the blues.";
  } else {
    max = 32;
    why = "Very low light. ISO 3200 is a trade of noise for a usable image.";
  }

  // A video light puts light directly on the subject, so — like
  // redGainPercent/blueGainPercent above — the deficit this is compensating
  // for doesn't grow with depth the way it does shooting on ambient alone.
  // Pushing a small action-cam sensor to ISO 3200 because the water column
  // is dark is a real trade; doing it with a light already pointed at the
  // subject is just noise for nothing. Only ever caps the ladder *down* —
  // shallow, bright ambient light is still the cleanest case there is,
  // light or no light, so this must never raise what ambient alone gives.
  if (c.light === "video_lights" && max > 8) {
    max = 8;
    why =
      "Video lights put light on the subject directly, so this doesn't " +
      "need to climb with depth the way ambient-only shooting does.";
  }

  if (c.visibility === "low") {
    max = Math.min(max * 2, 32);
    why += " Raised a stop because low visibility eats light.";
  }

  return setting(`i${max}M1`, "ISO range", why);
}

/** Longest shutter as 1/x. Slower gathers light; too slow smears motion. */
export function maxShutter(c: DiveConditions): number {
  const fps = c.frameRate;
  // Close subjects punish any blur.
  if (c.subject === "macro") return Math.max(fps * 4, 120);
  // Roughly a 180-degree shutter: all the light we can get.
  if (c.depthM > 25 || c.visibility === "low") return Math.max(Math.round(fps * 1.6), 48);
  return fps * 2;
}

function whiteBalanceSetting(c: DiveConditions): Token {
  if (c.light === "video_lights") {
    return setting(
      "w45",
      "White balance 4500K",
      "Video lights are roughly daylight-balanced, so a fixed 4500K keeps the " +
        "lit foreground neutral and lets the background go blue.",
    );
  }
  if (c.depthM <= 6) {
    return setting(
      "wA",
      "Auto white balance",
      "In the top few metres there is still enough red for auto to work.",
    );
  }
  if (c.camera === "HERO13") {
    return setting(
      "wN",
      "Native white balance",
      "Native keeps the sensor's raw response, which gives WBDV and the grade " +
        "the most to work with.",
    );
  }
  return setting(
    "wA",
    "Auto white balance",
    "Auto plus a WB lock beats a fixed Kelvin value once you are moving " +
      "through changing depth.",
  );
}

// --- extension tokens ------------------------------------------------------
//
// Every extension below is a *pair*: the value this preset wants, and the
// value that undoes it. Two problems this fixes, found by testing real
// hardware (see TOCONSIDER.md):
//
// 1. `buildTokens` used to only ever push the "on" branch, so a permanent
//    (or even session) value from one scanned preset would silently outlive
//    it — e.g. `*BITR=120` from a graded preset stayed active after scanning
//    a share-as-shot preset that never mentioned BITR at all, confirmed via
//    a HERO13 reboot overlay. Always pushing one branch or the other closes
//    that gap for the normal, single-QR-scan path.
// 2. `buildAdvancedTokens` below reuses the same pairs for the macro-based
//    "tied to this preset" mode: the "on" branch becomes the setup macro
//    body and the "off" branch becomes the restore macro body, so the
//    settings genuinely follow dial selection instead of freezing at
//    whatever was last scanned — which a *scanned* $/* token can never do,
//    confirmed by testing: picking a different preset off the physical dial
//    never re-runs anything from the QR that created it.

/** Shape shared by every extension pair whose on/off values, labels, and
 *  rationale don't depend on the dive conditions — everything below except
 *  red/blue gain (depend on water & depth) and the shutter cap (depends on
 *  frame rate). One spec plus one builder instead of one function per pair. */
interface StaticPairSpec {
  fourcc: Feature;
  onValue: string | number;
  onLabel: string;
  onRationale: string;
  offValue: string | number;
  offLabel: string;
  offRationale: string;
}

function staticPair(spec: StaticPairSpec, permanent: boolean): { on: Token; off: Token } {
  return {
    on: extension(spec.fourcc, spec.onValue, spec.onLabel, spec.onRationale, spec.fourcc, { permanent }),
    off: extension(spec.fourcc, spec.offValue, spec.offLabel, spec.offRationale, spec.fourcc, { permanent }),
  };
}

const WBLK_SPEC: StaticPairSpec = {
  fourcc: "WBLK",
  onValue: 1,
  onLabel: "Lock WB at capture",
  onRationale:
    "Locks white balance the moment you hit record, so a passing fish or a " +
    "sand patch cannot shift the whole shot.",
  offValue: 0,
  offLabel: "WB lock off",
  offRationale:
    "Video lights already hold a fixed colour temperature, so there is " +
    "nothing to lock — off explicitly, so a previous preset's lock can't " +
    "leak into this one.",
};
function wblkPair(permanent: boolean): { on: Token; off: Token } {
  return staticPair(WBLK_SPEC, permanent);
}

/** WBDV (HERO13) or WARM (other cameras) — camera decides the FourCC,
 *  `redGainPercent` decides the value on the "on" branch. Both live under
 *  one pair, keyed on whether any gain is needed at all, rather than two
 *  separate on/off pairs, since only one of the two tokens is ever relevant
 *  to a given camera. */
function redPair(c: DiveConditions, permanent: boolean): { on: Token; off: Token } {
  const red = redGainPercent(c);
  if (c.camera === "HERO13") {
    return {
      on: extension(
        "WBDV",
        1,
        "Depth-adaptive red",
        "Adds red progressively as the scene turns blue, so one code holds up " +
          "across a descent instead of only at one depth.",
        "WBDV",
        { permanent },
      ),
      off: extension(
        "WBDV",
        0,
        "Depth-adaptive red off",
        "No red gain needed at this depth — off explicitly, so a deeper " +
          "preset's push can't leak into a shallow one.",
        "WBDV",
        { permanent },
      ),
    };
  }
  return {
    on: extension(
      "WARM",
      red,
      "Red channel gain",
      `At ${c.depthM} m in ${c.water.replace("_", " ")} the red channel is down ` +
        `roughly this much. +${red}% brings it back.`,
      "WARM",
      { permanent },
    ),
    off: extension(
      "WARM",
      0,
      "Red channel gain off",
      "No red gain needed at this depth — off explicitly, so a deeper " +
        "preset's push can't leak into a shallow one.",
      "WARM",
      { permanent },
    ),
  };
}

function coolPair(c: DiveConditions, permanent: boolean): { on: Token; off: Token } {
  const blue = blueGainPercent(c);
  return {
    on: extension(
      "COOL",
      blue,
      "Blue channel gain",
      `Green water is short of blue as well as red. +${blue}% blue does ` +
        "digitally what a magenta filter does optically.",
      "COOL",
      { permanent },
    ),
    off: extension(
      "COOL",
      0,
      "Blue channel gain off",
      "No blue gain needed in this water — off explicitly, so a green-water " +
        "preset's push can't leak into clearer water.",
      "COOL",
      { permanent },
    ),
  };
}

const EXPN_SPEC: StaticPairSpec = {
  fourcc: "EXPN",
  onValue: 1000,
  onLabel: "Shortest shutter",
  onRationale:
    "Stops the camera choosing a needlessly fast shutter in bright shallows, " +
    "which would only strobe the surface caustics.",
  offValue: 0,
  offLabel: "Shortest shutter off",
  offRationale:
    "No shutter floor needed below the top few metres — off explicitly, so " +
    "a shallow preset's cap can't leak into a deeper one.",
};
function expnPair(permanent: boolean): { on: Token; off: Token } {
  return staticPair(EXPN_SPEC, permanent);
}

const GCMP_SPEC: StaticPairSpec = {
  fourcc: "GCMP",
  onValue: 1,
  onLabel: "No gyro exposure comp",
  onRationale:
    "Turns off the automatic shutter shortening the camera applies when it " +
    "senses motion. Divers move slowly, so this is close to a free stop.",
  offValue: 0,
  offLabel: "Gyro exposure comp restored",
  offRationale:
    "Video lights already give the shutter room to work with, so the " +
    "camera's own motion compensation stays on — off explicitly, so a " +
    "previous preset's override can't leak into this one.",
};
function gcmpPair(permanent: boolean): { on: Token; off: Token } {
  return staticPair(GCMP_SPEC, permanent);
}

const BITR_SPEC: StaticPairSpec = {
  fourcc: "BITR",
  onValue: 120,
  onLabel: "120 Mb/s",
  onRationale: "Survives a heavy white-balance push. Needs a card rated V30 or better.",
  offValue: 0,
  offLabel: "Bitrate boost off",
  offRationale:
    "Natural colour straight off the card doesn't need the extra bits — off " +
    "explicitly, so a graded preset's bitrate can't leak into this one. " +
    "Hardware-confirmed: a HERO13 reboot overlay showed no bitrate " +
    "override active after scanning *BITR=0.",
};
function bitrPair(permanent: boolean): { on: Token; off: Token } {
  return staticPair(BITR_SPEC, permanent);
}

/** Always emitted every scan already — no leak in the single-QR path — but
 *  advanced mode's restore macro still needs an explicit undo value, so the
 *  "off" half is exposed here too. EXPX has a documented disable value (0);
 *  DAMP's documented default is 1, not 0. */
function expxPair(c: DiveConditions, permanent: boolean): { on: Token; off: Token } {
  const shutter = maxShutter(c);
  return {
    on: extension(
      "EXPX",
      shutter,
      "Longest shutter",
      `Caps the shutter at 1/${shutter}s: slow enough to gather light, fast ` +
        "enough that fins and fish stay sharp.",
      "EXPX",
      { permanent },
    ),
    off: extension(
      "EXPX",
      0,
      "Longest shutter restored",
      "Back to the camera's own shutter ceiling when leaving this preset.",
      "EXPX",
      { permanent },
    ),
  };
}

const DAMP_SPEC: StaticPairSpec = {
  fourcc: "DAMP",
  onValue: 4,
  onLabel: "Exposure damping",
  onRationale:
    "Slows auto-exposure so the image stops pumping as you swim through shafts of light.",
  offValue: 1,
  offLabel: "Exposure damping restored",
  offRationale: "Back to the camera's own default damping (1) when leaving this preset.",
};
function dampPair(permanent: boolean): { on: Token; off: Token } {
  return staticPair(DAMP_SPEC, permanent);
}

/** No documented or tested "off" value exists for DIVE — its input range is
 *  `1` or a refractive index `1.3`–`1.45`, and `0` is outside that range and
 *  untested. So unlike every other extension here, DIVE has no restore half:
 *  `buildAdvancedTokens` includes it in the setup macro only, and leaving an
 *  advanced preset does not currently reset underwater HyperSmooth tuning.
 *  See TOCONSIDER.md. */
function diveToken(permanent: boolean): Token {
  return extension(
    "DIVE",
    1,
    "Underwater HyperSmooth",
    "Retunes stabilisation for the refractive index of water. Without it " +
      "HyperSmooth is doing its maths for air.",
    "DIVE",
    { permanent },
  );
}

/** The plain camera settings — mode, format, lens, colour, white balance
 *  choice, ISO, stabilisation level, and (bitrate's plain-setting half) —
 *  hardware-confirmed to survive both a reboot and switching to a different
 *  preset on the physical dial, unlike every extension token above. Shared
 *  between `buildTokens` and `buildAdvancedTokens`: this half of a preset
 *  never needed a persist choice or a macro to stick. */
function coreTokens(c: DiveConditions): Token[] {
  const t: Token[] = [];

  t.push(setting("mV", "Video mode", "Dive presets are video-first."));

  // Falls back to 4K 16:9 — the one combination every camera supports — if a
  // saved preset predates this field or names a combination with no token.
  const res =
    RESOLUTION_TOKENS[c.resolution][c.aspect] ?? RESOLUTION_TOKENS["4k"]["16:9"]!;
  t.push(setting(res.text, res.label, res.rationale, res.feature));

  const fps = c.frameRate;
  t.push(
    setting(
      `p${fps}`,
      `${fps} fps`,
      fps <= 30
        ? "30p is the light-gathering sweet spot below the surface. Save 60p for bright shallows."
        : c.depthM > 20
          ? "60p costs light you don't have this deep — see the warning above."
          : "Bright water, so a higher frame rate is affordable.",
    ),
  );

  // --- lens ---------------------------------------------------------------
  if (c.visibility === "low") {
    t.push(
      setting(
        "fL",
        "Linear",
        "In poor visibility a wide lens just photographs more particulate. " +
          "Linear crops in and cuts backscatter at the frame edges.",
      ),
    );
  } else if (c.subject === "macro") {
    t.push(
      setting(
        "fL",
        "Linear",
        "Linear removes the barrel distortion that makes close subjects bulge.",
      ),
    );
  } else {
    t.push(
      setting("fW", "Wide", "Wide gets you close, and close is how you shoot through less water."),
    );
  }

  // --- colour -------------------------------------------------------------
  if (c.workflow === "graded") {
    t.push(
      setting(
        "cF",
        "Flat colour",
        "Flat holds shadow and highlight detail for grading. Do not use it if " +
          "you plan to share the clip untouched.",
      ),
    );
    t.push(
      setting(
        "d1",
        "10-bit",
        "10-bit stops the blues banding when you push white balance in post.",
        "tenbit",
      ),
    );
  } else {
    t.push(setting("cN", "Natural colour", "Natural looks right straight off the card."));
  }

  t.push(whiteBalanceSetting(c));
  t.push(isoToken(c));
  t.push(evToken(c));

  t.push(
    setting(
      "e2",
      "HyperSmooth High",
      "High is the useful setting underwater. Boost crops more than the reduced " +
        "shake is worth.",
    ),
  );

  if (c.workflow === "graded") {
    t.push(setting("b1", "High bitrate", "Flat footage needs the bits."));
  }

  return t;
}

interface ExtensionPairs {
  wblk: { on: Token; off: Token };
  red: { on: Token; off: Token };
  cool: { on: Token; off: Token };
  expx: { on: Token; off: Token };
  expn: { on: Token; off: Token };
  gcmp: { on: Token; off: Token };
  damp: { on: Token; off: Token };
  bitr: { on: Token; off: Token };
}

/** Builds every extension pair exactly once. `buildTokens` only ever needs
 *  one branch of each, but `buildAdvancedTokens` needs the setup selection
 *  *and* the restore (all-off) side from the same pairs — computing them
 *  once here and passing the result around avoids building each pair twice. */
function buildExtensionPairs(c: DiveConditions, permanent: boolean): ExtensionPairs {
  return {
    wblk: wblkPair(permanent),
    red: redPair(c, permanent),
    cool: coolPair(c, permanent),
    expx: expxPair(c, permanent),
    expn: expnPair(permanent),
    gcmp: gcmpPair(permanent),
    damp: dampPair(permanent),
    bitr: bitrPair(permanent),
  };
}

/** The extension tokens a preset actually wants right now — every
 *  conditional one resolved to its "on" or "off" branch, so the payload is
 *  always a complete, self-contained statement of intent rather than
 *  silently trusting whatever a previous scan left active. */
function selectExtensionTokens(c: DiveConditions, permanent: boolean, pairs: ExtensionPairs): Token[] {
  return [
    c.light === "video_lights" ? pairs.wblk.off : pairs.wblk.on,
    redGainPercent(c) ? pairs.red.on : pairs.red.off,
    blueGainPercent(c) ? pairs.cool.on : pairs.cool.off,
    pairs.expx.on,
    c.depthM <= 8 ? pairs.expn.on : pairs.expn.off,
    c.light !== "video_lights" ? pairs.gcmp.on : pairs.gcmp.off,
    pairs.damp.on,
    diveToken(permanent),
    c.workflow === "graded" ? pairs.bitr.on : pairs.bitr.off,
  ];
}

function extensionTokens(c: DiveConditions, permanent: boolean): Token[] {
  return selectExtensionTokens(c, permanent, buildExtensionPairs(c, permanent));
}

/** `$PRES="<icon>,<name>"` — shared by the plain and dial-mode preset paths
 *  below, which differ only in the name's source and why it's worth telling
 *  the diver about. */
function presetToken(name: string, icon: number, rationale: string): Token {
  return extension("PRES", `${icon},${name}`, "Named preset", rationale, "PRES", { quoted: true });
}

export function buildTokens(c: DiveConditions): Token[] {
  const permanent = c.persistenceMode === "permanent";
  const t = [...coreTokens(c), ...extensionTokens(c, permanent)];

  // --- on-camera preset ---------------------------------------------------
  const preset = cleanPresetName(c.presetName ?? "");
  if (preset) {
    t.push(
      presetToken(
        preset,
        c.presetIcon ?? DEFAULT_PRESET_ICON,
        `Creates a preset called “${preset}” on the camera, so you can pick it ` +
          "from the dial without rescanning at the dive site. Unlike the rest " +
          "of this code it outlasts a power cycle — it stays on the camera " +
          "until you delete it there.",
      ),
    );
  }

  return t;
}

export interface AdvancedTokens {
  /** Plain settings, applied once at scan time and captured by the camera's
   *  own preset system — no macro needed. */
  core: Token[];
  /** Runs when this preset is selected on the dial. */
  setup: Token[];
  /** Runs when a *different* preset is selected — i.e. when leaving this
   *  one. Does not include DIVE; see `diveToken`'s comment. */
  restore: Token[];
  /** `$PRES="<icon>,<macroName>"` — the macro name doubles as the dial
   *  label, which is why it is constrained to `cleanMacroName`'s 4
   *  uppercase letters rather than the free text `cleanPresetName` allows. */
  preset: Token;
}

/**
 * The "tied to this preset" persistence mode: instead of scanning extension
 * tokens once and hoping nothing switches presets afterward, wrap them in a
 * GoPro Labs macro pair keyed to the preset's own name, so selecting a
 * different preset on the physical dial actually runs the undo commands.
 * Reference: `*STUF="$EXPN=120..."` (setup) / `*STUf="$EXPN=0..."` (restore,
 * same name with the last letter lowercased) / `$PRES="0,STUF"` (the
 * preset itself), from https://gopro.github.io/labs/control/tech/.
 *
 * Every extension token here is always `$` (temporary), never `*`, even
 * though the macros that store them are always `*` (permanent) — the macro
 * re-runs its body fresh every time it fires, so there is nothing left for
 * session-vs-permanent to mean. `persistenceMode` only matters for the two
 * non-"dial" modes; this function ignores it entirely.
 */
export function buildAdvancedTokens(c: DiveConditions, macroName: string): AdvancedTokens {
  const core = coreTokens(c);
  const pairs = buildExtensionPairs(c, false);
  const setup = selectExtensionTokens(c, false, pairs);
  // Every pair's "off" branch, in the same order buildExtensionPairs built
  // them in — derived rather than re-listed, so a pair added there doesn't
  // also need to be remembered here. DIVE deliberately excluded — it isn't
  // one of these pairs; see diveToken's comment.
  const restore = Object.values(pairs).map((pair) => pair.off);
  const preset = presetToken(
    macroName,
    c.presetIcon ?? DEFAULT_PRESET_ICON,
    `Creates a preset called “${macroName}” on the camera that also runs the ` +
      "setup macro when selected, and the restore macro when you switch away " +
      "from it — so these settings actually follow the dial instead of " +
      "freezing at whatever was last scanned.",
  );
  return { core, setup, restore, preset };
}

/** Labs caps a preset name at 15 characters. */
export const MAX_PRESET_NAME = 15;

/**
 * The name is interpolated into `$PRES="14,…"`, so a quote in it would end the
 * string early and produce a payload that either fails to parse or means
 * something else. Stripped rather than escaped: Labs has no escape syntax here.
 *
 * Deliberately NOT uppercased. GoPro's own example is "Scuba", and a preset
 * name is parsed for FourCC commands — so upcasing a diver's "dive wreck" into
 * "DIVE WRECK" would silently arm the DIVE extension. `lintPresetName` in
 * compiler.ts warns about the names that do this; this function just avoids
 * manufacturing them.
 */
export function cleanPresetName(raw: string): string {
  return raw
    .replace(/["“”]/g, "")
    .trim()
    .slice(0, MAX_PRESET_NAME)
    .trim();
}

/** GoPro's own reference: "Macros are saved in string FourCCs" — every
 *  example it gives (STUF, SUBA, RUNN, LVAL) is exactly 4 uppercase letters.
 *  A macro-tied preset's dial name has to equal the macro's own key for the
 *  on-select/on-deselect pairing to fire, so unlike `cleanPresetName` this
 *  uppercases and restricts to letters — there is no free-text option once
 *  "tied to this preset" is chosen. */
export const MAX_MACRO_NAME = 4;

export function cleanMacroName(raw: string): string {
  return raw
    .toUpperCase()
    .replace(/[^A-Z]/g, "")
    .slice(0, MAX_MACRO_NAME);
}

/** The restore macro pairs with its setup macro by lowercasing the setup
 *  macro's *last* character only — `STUF` / `STUf`, per GoPro's reference —
 *  not the whole name. */
export function restoreMacroName(macroName: string): string {
  return macroName.slice(0, -1) + macroName.slice(-1).toLowerCase();
}
